/*global google*/
import React, { useState, useEffect, createContext, memo, useContext, useMemo, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import SidePanel from "../../../components/SidePanel";
import {
  checkForLatLong,
  convertArrayToStringWithDelimeter,
  getArrangeMonthOrder
} from "../../../utils/helper";
import {
  getCapacityTooltipData,
  getCapacityGeohashes
} from "../../../services/api/mapGeohashes.api";
import { getPolygonByAdminLevel } from "../../../services/api/mapLocation.api";
import { getFilters } from "../../../services/api/filterService";
import {
  SupplyType,
  supplyLvlMapper,
  supplyLvlInfoMapper,
  DemandScoreTypes,
  boxTolerance,
  supplyCustOpFiltersMap,
  EdotcoSiteIconLegend,
  OperatorSiteIconLegend,
  connectionTypes,
  formatSupplyLevel,
  geohashDefaultZoomLvl,
  sitesDefaultZoomLvl,
  SEARCH_AREA_STATES,
  clusterDefaultZoomLvl,
  MapTools,
  Names_For_Filter_OPs,
  Sections,
} from "../../../constants/WebMap";
import Legend from "../../../components/Legend";
import { getMapGlobalConfig, getOperatorsByQuarter } from "../../../services/api/mapOperators";
import { convertGeohashToPolygon } from "../../../utils/helper";
import MeasureToolTotalStats from "../../../components/elements/MeasureToolTotalStats";
import { LoadMapContext } from "./LoadWebMap";
import WebMapNoData from "../../../components/WebMapNoData";
import { pageSizeByAdmLevels } from "../../../constants/WebMapApi";
import { getPolygonFromApiResp, paginatedApiInvoker } from "../../../utils/webMapApiHelper";
import EdotcoSitesSection from "./map-sections/EdotcoSitesSection";
import OperatorSitesSection from "./map-sections/OperatorSitesSection";
import ProposedSitesSection from "./map-sections/ProposedSitesSection";
import MapToolsSection from "./map-sections/MapToolsSection";
import OprtSiteTooltipSection from "./map-sections/OprtSiteTooltipSection";
import EdotcoSiteTooltipSection from "./map-sections/EdotcoSiteTooltipSection";
import SupplyDemandTooltipSection from "./map-sections/SupplyDemandTooltipSection";
import DemandGeohashSection from "./map-sections/DemandGeohashSection";
import SupplyGeohashSection from "./map-sections/SupplyGeohashSection";
import GeoAreaSection from "./map-sections/GeoAreaSection";
import SearchOnMapSection from "./map-sections/SearchOnMapSection";
import GoogleMapView from "./map-sections/GoogleMapView";
import CapacityChart from "../../../components/elements/CapacityChart";
import CapacityGeohashSection from "./map-sections/CapacityGeohashSection";
import CapacityTooltipSection from "./map-sections/CapacityTooltipSection";
import { getCapacityChartData, getCapacitySubFilters } from "../../../services/api/capacityService";
import CurrentZoomLabel from "../../../components/CurrentZoomLabel";
import WebMapTopNotifications from "../../../components/WebMapTopNotifications";
import { removeDuplicateGeoHashes } from "../../../helpers/webMap";
import { fetchBuildingLocatorSearch } from "../../../services/api/mapIBS.api";
import UploadedFilesSection from "./map-sections/UploadedFilesSection";
import { BackDrop } from "../../../components/elements/Loader";
import { getAnonymizedOperatorsByCountry } from "../../../services/api/anonymizedOperators";
import { defaultGradientArr } from "../../../constants/Data";

export const WebMapContext = createContext({});

const WebMapGoogleMap = ({
  mapRef,
  initMap,
  updateCenter,
  updateZoom,
  mapPlacement,
  setCommonLocationLevels,
  setCommonEyeIcons,
  initMapSettings = { zoom: 4.5, center: { lat: 16.26621385544778, lng: 89.85827047921973 } },
  setCommonZoomLvls,
  commonZoomLvls
}) => {
  const navigate = useNavigate();
  const { mapView } = useContext(LoadMapContext);

  const user = JSON.parse(localStorage.getItem("user"));
  const preferedZoomLevels = user?.preferedZoomLevels ? user.preferedZoomLevels : [];
  const preferedGeohashZoom = preferedZoomLevels.length !== 0 ? preferedZoomLevels.find(level => level.type === "geohash")?.zoomL : geohashDefaultZoomLvl;
  const preferedSiteZoom = preferedZoomLevels.length !== 0 ? preferedZoomLevels.find(level => level.type === "site")?.zoomL : sitesDefaultZoomLvl;
  const preferredDownloadZoom = 14;

  const [mapTypeId, setMapTypeId] = useState("terrain");
  const [boundsChanged, setBoundsChanged] = useState(0); // track whether ne/sw bound (bbox) changed => (randomCount)
  const [lastDataLoadedBound, setLastDataLoadedBound] = useState(null); // last bounds where any section (supply, demand, etc.) has loaded data
  const [mapZoomLevel, setMapZoomLevel] = useState(initMapSettings.zoom);
  const [appliedFilters, setAppliedFilters] = useState({
    country: null,
    countryCode: null,
    level: null,
    levelVal: null,
    year: null,
    quarter: null,
    adminLevel1: null,
    adminLevel2: null,
    adminLevel3: null,
    geohash: null
  });
  const [minMaxSupplyOp, setMinMaxSupplyOp] = useState([]);
  const [focusedPolygon, setFocusedPolygon] = useState([]);
  const [areaGeohashPolygon, setAreaGeohashPolygon] = useState([]);
  const [supplyFilters, setSupplyFilters] = useState({
    isBestOperator: true,
    operator: null,
    supplyType: SupplyType.RSRP,
    noData: false,
    connectionType: { best: null, custom: null },
    aggrType: { best: null, custom: null },
    dataType: { best: null, custom: null },
  });
  const [demandFilters, setDemandFilters] = useState({
    demandType: DemandScoreTypes.FINAL,
    range: [0, 1],
    operator: null,
    noData: false,
    connectionType: null,
    dataType: null,
    arrgType: null,
    strengthLevels: {}
  });
  const [eyeIcons, setEyeIcons] = useState({
    adminLevel: false,
    supply: false,
    demand: false,
    sites: false,
    capacity: false,
    marketShare: false,
    ibs: false
  });
  const [tooltipData, setTooltipData] = useState();
  const [edotcoSiteTooltipData, setEdotcoSiteTooltipData] = useState(null);
  const [operatorSiteTooltipData, setOperatorSiteTooltipData] = useState();
  const [operatorDetails, setOperatorDetails] = useState([]);
  const [defaultZoomLevels, setDefaultZoomLevels] = useState([]);
  const [operatorKeyWord, setOperatorKeyWord] = useState('Operators');
  const [siteLegend, setSiteLegend] = useState([]);
  const [supplyLegend5G, setSupplyLegend5G] = useState([]);
  const [supplyLegend4G, setSupplyLegend4G] = useState([]);
  const [supplyLegend3G, setSupplyLegend3G] = useState([]);
  const [demandLegend, setDemandLegend] = useState([]);
  const [capacityLegend, setCapacityLegend] = useState([]);

  const [supplyStrengthLevels, setSupplyStrengthLevels] = useState({});
  const [markerOnMap, setMarkerOnMap] = useState(null);
  const [geohashOnMap, setGeohashOnMap] = useState(null);
  const [selectedEdotcoSiteOperator, setSelectedEdotcoSiteOperator] =
    useState({ operatorList: [], value: null });
  const [selectedProposedOperator, setSelectedProposedOperator] =
    useState({ operatorList: [], value: null });
  const [selectedOperatorSiteOperator, setSelectedOperatorSiteOperator] =
    useState("op1");
  const [demandSupplyLegendData, setDemandSupplyLegendData] = useState([]);
  const [edotcoSiteVisible, setedotcoSiteVisible] = useState(false);
  const [proposedSiteVisible, setProposedSiteVisible] = useState(false);
  const [operatorSiteVisible, setOperatorSiteVisible] = useState(false);
  const [ibsVisible, setIBSVisible] = useState(false);
  const [edotcoSiteLegendData, setEdotcoSiteLegendData] = useState([]);
  const [operatorSiteLegendData, setOperatorSiteLegendData] = useState([]);
  const [marketShareLegendData, setMarketShareLegendData] = useState([]);
  const [ibsLegendData, setIBSLegendData] = useState([]);
  const [capacityLegendData, setCapacityLegendData] = useState([]);
  const [geohashNE, setGeohashNE] = useState([]); // northEastBoundry of adm lvl geohash
  const [geohashSW, setGeohashSW] = useState([]); // southWestBoundry of adm lvl geohash
  const [affluenceLvlFilters, setAffluenceLvlFilters] = useState([]);
  const [supplyLvlFilters, setSupplyLvlFilters] = useState({
    [SupplyType.RSRP]: [],
    [SupplyType.UPLOAD]: [],
    [SupplyType.DOWNLOAD]: [],
    [SupplyType.DATA_RECEIVED]: [],
    [SupplyType.DATA_TRANSMIT]: [],
    [SupplyType.DATA_TRANSCEIVED]: [],
  });
  const [toolMarkers, setToolMarkers] = useState([]);
  const [mapTool, setMapTool] = useState(null);
  const [selectedUploadedFile, setSelectedUploadedFile] = useState(null);
  const [triggerFetchUploadedFiles, setTriggerFetchUploadedFiles] = useState(false);
  const [apisOngoing, setApisOngoing] = useState(0);
  const [selectedSiteRef, setSelectedSiteRef] = useState([]);
  const [selectedSiteRefDropDown, setSelectedSiteRefDropDown] = useState();
  const [selectedPolygons, setSelectedPolygons] = useState([]);
  const [capacityChartData, setCapacityChartData] = useState({});
  const [selectedCapacityGeohashes, setSelectedCapacityGeohashes] = useState([]);
  const [capacityGeohashes, setCapacityGeohashes] = useState([]);
  const [capacityTooltipData, setCapacityTooltipData] = useState(null);
  const [edotcoSiteAreaToggle, setEdotcoSiteAreaToggle] = useState(false);
  const [opSiteAreaToggle, setOpSiteAreaToggle] = useState(false);
  const [geohashZoom, setGeohashZoom] = useState(preferedGeohashZoom);
  const [siteZoom, setSiteZoom] = useState(preferedSiteZoom);
  const [edotcoSubFilters, setEdotcoSubFilters] = useState({
    status: [],
    action: [],
    connectionType: "",
    dataType: "",
  });
  const [capacityFilters, setCapacityFilters] = useState({
    selectedYear: null,
    selectedOprtr: null,
    selectedMonth: null,
    selectedTower: null,
    selectedSector: null,
    selectedFlag: [],
    selectedAction: [],
    selectedCapacityLvl: null,
    selectedCapacityLvlVal: null,
    selectedCapacitySubLvl: null
  })
  const [capacityFilterData, setCapacityFilterData] = useState({
    years: [],
    operators: [],
    months: [],
    towers: [],
    sectors: [],
    towerScroll: 1,
    sectorScroll: 1
  })
  const [capacitySubFilters, setCapacitySubFilters] = useState({
    flags: {},
    actions: {},
  })
  const [selectedCapacityAdvanceData, setSelectedCapacityAdvanceData] = useState([])
  const [capacityAdvanceDisable, setCapacityAdvanceDisable] = useState(true)
  const [searchAreaBtn, setSearchAreaBtn] = useState(SEARCH_AREA_STATES.INIT);
  const [capacityChartConfig, setCapacityChartConfig] = useState(null)
  const [isLegendMinimized, setIsLegendMinimized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [globalConfig, setGlobalConfig] = useState({});
  const [enabledModules, setEnabledModules] = useState({});
  const [msConfig, setMsConfig] = useState([]); // market share config (from configs db)
  const [msConfigType, setMsConfigType] = useState({});
  const [ibsConfig, setIBSConfig] = useState([])
  const [msTableColorConfig, setMsTableColorConfig] = useState({});
  const [msGeohashOnMap, setMsGeohashOnMap] = useState(false); // whether market share geohash visualized on map
  const [quadLabelsConfig, setQuadLabelsConfig] = useState({}); // in market-share these give quad labels use to rename existing labels
  const permissions = JSON.parse(localStorage.getItem("user"))?.permissions;
  const [selectedCountryPremissions, setSelectedCountryPremssions] = useState([]);
  const [bestOperatorsPermitted, setPermittedBestOpsForSupply] = useState([]);
  const [operatorsPerfPermittedSupply, setPermittedOpsForSupply] = useState([]);
  const [operatorsPermittedDemand, setPermittedOpsForDemand] = useState([]);
  const [operatorsPermittedCapacity, setPermittedOpsForCapacity] = useState([]);
  const [operatorsPermittedOpSite, setPermittedOpsForOpSite] = useState([]);
  const [operatorsPermittedECSiteET, setPermittedOpsForECSiteET] = useState([]);
  const [operatorsPermittedECSitePT, setPermittedOpsForECSitePT] = useState([]);
  const [operatorsPermittedIBS, setPermittedOpsForIBS] = useState([]);
  const [operatorsPermittedBestIBS, setPermittedOpsForBestIBS] = useState([]);
  const [operatorsPermittedMS, setPermittedOpsForMS] = useState([]);
  const [operatorsPermittedTopMS, setPermittedOpsForTopMS] = useState([]);
  const [downloadData, setDownloadData] = useState({ sitesEdotco: [], sitesOperator: [], ibs: [] });
  const [downloadProperties, setDownloadProperties] = useState({ radius: 1, coordinates: [] });
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    hasPermissionToWebMap();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (mapZoomLevel < geohashZoom) {
      setCapacityGeohashes([]);
      const { supply, demand, capacity, marketShare, ibs } = eyeIcons;
      if ((supply || demand || capacity || (marketShare && msGeohashOnMap) || ibs) && geohashZoom && (mapZoomLevel === geohashZoom - 1)) setLastDataLoadedBound(null); // applied to supply/deman/cap sections
    }
  }, [mapZoomLevel, geohashZoom]);

  // trigger when country / year / quarter change
  useEffect(() => {
    const { country, year, quarter } = appliedFilters;
    if (country && year && quarter) {
      fetchOperators(appliedFilters);
    }
    // reset when country / year / quarter change
    if (mapPlacement === 'left' || (mapPlacement === 'right' && !appliedFilters.country)) {
      // setFocusedPolygon([]);
      // setAreaGeohashPolygon([]);
    }
    setCapacityGeohashes([]);
    setSupplyLvlFilters({
      [SupplyType.RSRP]: [],
      [SupplyType.UPLOAD]: [],
      [SupplyType.DOWNLOAD]: [],
      [SupplyType.DATA_RECEIVED]: [],
      [SupplyType.DATA_TRANSMIT]: [],
      [SupplyType.DATA_TRANSCEIVED]: [],
    });

    setSupplyFilters((prev) => ({ ...prev, connectionType: { best: null, custom: null }, dataType: { best: null, custom: null }, aggrType: { best: null, custom: null } }));
    // setEyeIcons((prev) => ({
    //   ...prev,
    //   // adminLevel: mapPlacement === 'right' && appliedFilters.country ? eyeIcons.adminLevel : false,
    //   // supply: false,
    //   // demand: false,
    //   // sites: false,
    //   // capacity: false,
    //   // marketShare: false,
    //   // ibs: false
    // }));
    setToolMarkers([]);

    setSelectedPolygons([]);
    setSelectedSiteRef([]);
    setDemandSupplyLegendData([]);
  }, [appliedFilters.country, appliedFilters.year, appliedFilters.quarter]);

  useEffect(() => {
    setSupplyFilters((prev) => ({ ...prev, isBestOperator: true, connectionType: { best: null, custom: null }, dataType: { best: null, custom: null }, aggrType: { best: null, custom: null } }));
    setEyeIcons((prev) => ({
      ...prev,
      adminLevel: mapPlacement === 'right' && appliedFilters.country ? eyeIcons.adminLevel : false,
      supply: false,
      demand: false,
      sites: false,
      capacity: false,
      marketShare: false,
      ibs: false
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters.country])

  useEffect(() => {
    fetchGlobalConfig(appliedFilters.countryCode)
  }, [appliedFilters.countryCode]);

  useEffect(() => {
    if (tooltipData) setTooltipData();
    if (capacityTooltipData) setCapacityTooltipData();
    // if (appliedFilters.level !== 'geoHashLevel' && areaGeohashPolygon.length) setAreaGeohashPolygon([]);
  }, [appliedFilters]);

  // set bounds of adm lvl geohash
  useEffect(() => {
    if (appliedFilters.level === 'geoHashLevel' && appliedFilters.levelVal) {
      const geoPoly = convertGeohashToPolygon(appliedFilters.levelVal);
      setGeohashNE([geoPoly[1].lng, geoPoly[1].lat]);
      setGeohashSW([geoPoly[3].lng, geoPoly[3].lat]);
    } else {
      setGeohashNE([]);
      setGeohashSW([]);
    }
  }, [appliedFilters.levelVal]);

  // clear site-ref selection when changing geohash area data
  useEffect(() => {
    if (edotcoSiteAreaToggle) setSelectedSiteRef([]);
  }, [appliedFilters.levelVal, edotcoSiteAreaToggle]);

  useEffect(() => {
    if (operatorDetails?.length > 0) {
      if (!supplyFilters.operator) {
        const selectedOp = operatorsPerfPermittedSupply.find(op => op.operator === operatorDetails[0]?.operator);
        const anom = {
          isAnonymized: selectedOp?.isAnonymized,
          name: selectedOp?.name
        }
        setSupplyFilters((prev) => ({
          ...prev,
          operator: operatorDetails[0]?.operator,
          anom
        }));
      }
      if (!demandFilters.operator)
        setDemandFilters({
          ...demandFilters,
          operator: operatorDetails[0]?.operator,
        });
    }
    // eslint-disable-next-line
  }, [operatorDetails]);

  // common eyeIcons (only from left map)
  useEffect(() => {
    if (setCommonEyeIcons) setCommonEyeIcons(eyeIcons);
  }, [eyeIcons.adminLevel]);

  useEffect(() => {
    setRefresh((prev) => !prev)
  }, [appliedFilters.year, appliedFilters.quarter]);

  useEffect(() => {
    generateSupplyLegend(
      supplyFilters.isBestOperator,
      eyeIcons.supply && geohashZoom && mapZoomLevel >= geohashZoom
    );
  }, [eyeIcons.supply, supplyFilters, mapZoomLevel, minMaxSupplyOp]);

  useEffect(() => {
    generateSupplyLegend(
        supplyFilters.isBestOperator,
        eyeIcons.supply && geohashZoom && mapZoomLevel >= geohashZoom
    );
  }, [bestOperatorsPermitted, geohashZoom]);

  useEffect(() => {
    generateDemandLegend(
      demandFilters.demandType,
      eyeIcons.demand && geohashZoom && mapZoomLevel >= geohashZoom
    );
  }, [eyeIcons.demand, demandFilters.demandType, mapZoomLevel, refresh, demandLegend]);

  useEffect(() => {
    setSupplyLvlFilters({
      [SupplyType.RSRP]: [],
      [SupplyType.UPLOAD]: [],
      [SupplyType.DOWNLOAD]: [],
      [SupplyType.DATA_RECEIVED]: [],
      [SupplyType.DATA_TRANSMIT]: [],
      [SupplyType.DATA_TRANSCEIVED]: [],
    });
    if (!supplyFilters.isBestOperator && supplyFilters.connectionType.custom && supplyFilters.dataType.custom )
      fetchAllSupplyLvlFilters();
  }, [
    supplyFilters.isBestOperator,
    supplyFilters.connectionType.custom,
    supplyFilters.dataType.custom,
    supplyFilters.aggrType.custom,
  ])
  
  useEffect(() => {
    if (appliedFilters.level === 'geoHashLevel' && appliedFilters.levelVal) {
      const geoPoly = convertGeohashToPolygon(appliedFilters.levelVal);
      setAreaGeohashPolygon(geoPoly);
      fitPolygonBounds([geoPoly]);
    }
  }, [appliedFilters.level, appliedFilters.levelVal]);

  useEffect(() => {
    if (selectedSiteRef.length !== 0) {
      const polygons = selectedSiteRef.map((location) => {
        let lonlat = {
          lng: location.longitude,
          lat: location.latitude,
        };
        return [lonlat];
      });

      if (selectedSiteRef.length > 1 && polygons.length) {
        fitPolygonBounds(polygons);
      } else if (mapRef.current) { // length = 1
        mapRef.current.setCenter(new google.maps.LatLng(selectedSiteRef[0].latitude, selectedSiteRef[0].longitude));
      }
    }
  }, [selectedSiteRef]);

  useEffect(() => {
    if (selectedCapacityGeohashes?.length === 0 || capacityGeohashes?.length === 0) {
      if (capacityTooltipData) setCapacityTooltipData(null);
    }

    if (selectedCapacityGeohashes.length !== 0) {
      let polygons = []
      if (selectedCapacityGeohashes.length === 1) {
        polygons = selectedCapacityGeohashes.map((location, idx) => {

          let coordinate1 = location.nwCoordinate
          let coordinate2 = location.seCoordinate

          coordinate1.lng += 0.002;
          coordinate1.lat += 0.002;
          coordinate2.lng -= 0.002;
          coordinate2.lat -= 0.002;

          return [coordinate1, coordinate2];
        });
      } else {
        polygons = selectedCapacityGeohashes.map((location) => {
          return [location.nwCoordinate, location.seCoordinate];
        });
      }

      if (polygons.length) {
        fitPolygonBounds(polygons);
      }
    }
  }, [selectedCapacityGeohashes]);

  //clear capacitytower and sector data in admin level reset
  useEffect(() => {
    if (((appliedFilters.level !== null && appliedFilters.levelVal !== null) ||
      (mapZoomLevel >= geohashZoom))
    ) {
      setCapacityFilters({
        ...capacityFilters,
        selectedTower: null,
        selectedSector: null,
        selectedCapacityLvl: "operator",
        selectedCapacityLvlVal: capacityFilters.selectedOprtr,
        selectedFlag: [],
        selectedAction: [],
        selectedCapacitySubLvl: null
      });

      setCapacityFilterData((prev) => ({
        ...prev,
        towerScroll: 1,
        sectorScroll: 1,
        sectors: [],
      }));
      setSelectedCapacityAdvanceData([]);

      if (!appliedFilters.level && !appliedFilters.levelVal) {
        onChangeCapacitySubFilters()
      }
    }
  }, [
    appliedFilters.level,
    appliedFilters.levelVal,
  ]);

  // clear all capacity filters in country reset
  useEffect(() => {
    setCapacityFilters({
      ...capacityFilters,
      selectedYear: null,
      selectedOprtr: null,
      selectedMonth: null,
      selectedTower: null,
      selectedSector: null,
      selectedCapacityLvl: null,
      selectedCapacityLvlVal: null,
      selectedCapacitySubLvl: null
    });

    setCapacityFilterData((prev) => ({
      ...prev,
      sectorScroll: 1,
      towerScroll: 1,
      years: [],
      months: [],
      operators: [],
      sectors: [],
      towers: []
    }));

    setCapacitySubFilters({
      flags: {},
      actions: {},
    })
    setSelectedCapacityAdvanceData([]);
  }, [appliedFilters.countryCode])

  // capacity triggers
  // trigger when admin level selected or bbox changed
  useEffect(() => {
    loadCapacityBoundChange();
  }, [
    eyeIcons.capacity,
    capacityFilters.selectedCapacityLvl,
    capacityFilters.selectedCapacityLvlVal,
    capacityFilters.selectedCapacitySubLvl,
    capacityFilters.selectedFlag,
    capacityFilters.selectedAction,
    selectedCapacityAdvanceData,
    capacityAdvanceDisable,
    appliedFilters.level,
    appliedFilters.levelVal,
    geohashZoom,
    geohashNE,
    boundsChanged
  ]);

  // capacity chart
  useEffect(() => {
    if (capacityFilters.selectedYear && capacityFilters.selectedOprtr && capacityFilters.selectedMonth && eyeIcons.capacity && capacityAdvanceDisable) {
      fetchCapacityChartData();
    }
  }, [
    eyeIcons.capacity,
    capacityFilters.selectedOprtr,
    capacityAdvanceDisable,
    appliedFilters.level,
    appliedFilters.levelVal,
    mapZoomLevel,
    boundsChanged
  ]);

  // trigger search area button
  useEffect(() => {
    if (searchAreaBtn === SEARCH_AREA_STATES.FETCH) {
      loadCapacityBoundChange();
      fetchCapacityChartData();
    }
    // eslint-disable-next-line
  }, [searchAreaBtn]);

  // to display tower and sector geohashes
  useEffect(() => {
    if (capacityAdvanceDisable && appliedFilters.countryCode && capacityFilters.selectedYear &&
      capacityFilters.selectedOprtr && capacityFilters.selectedMonth && eyeIcons.capacity &&
      selectedCapacityAdvanceData.length === 0 && (capacityFilters.selectedCapacityLvl === 'enodbID' ||
        capacityFilters.selectedCapacityLvl === 'secID')
    ) {
      if (!capacityFilters.selectedCapacitySubLvl) {
        onChangeCapacitySubFilters()
      } else {
        setSelectedCapacityGeohashes([])
        setCapacityGeohashes([])
        fetchCapacityData();
        generateCapacityLegend(eyeIcons.capacity)
      }
    } else {
      if (selectedCapacityAdvanceData.length === 0) setSelectedCapacityGeohashes([])
    }
  },
    [
      eyeIcons,
      capacityFilters.selectedCapacityLvl,
      capacityFilters.selectedCapacityLvlVal,
      capacityFilters.selectedCapacitySubLvl,
      capacityFilters.selectedFlag,
      capacityFilters.selectedAction,
      selectedCapacityAdvanceData,
      capacityAdvanceDisable
    ]);

  useEffect(() => {
    if (mapZoomLevel > geohashZoom &&
      !(capacityFilters.selectedCapacityLvl === 'enodbID' || capacityFilters.selectedCapacityLvl === 'secID') &&
      selectedCapacityGeohashes.length === 0
    ) {
      onChangeCapacitySubFilters()
    }
  }, [mapZoomLevel])

  useEffect(() => {
    eyeIcons.capacity ? setDemandSupplyLegendData([]) : setCapacityLegendData([]);
    if (!eyeIcons.capacity || !capacityFilters.selectedOprtr || !appliedFilters.level || !appliedFilters.levelVal
      || !capacityAdvanceDisable || (mapZoomLevel < geohashZoom)) {
      setCapacityGeohashes([])
      setCapacityChartData([])
    }
  }, [
    eyeIcons.capacity,
    capacityFilters.selectedOprtr,
    appliedFilters.level,
    appliedFilters.levelVal,
    capacityAdvanceDisable
  ])

  useEffect(() => { // set common zoom lvls to use in right map
    if (setCommonZoomLvls) setCommonZoomLvls({ geohash: geohashZoom, sites: siteZoom });
  }, [geohashZoom, siteZoom]);

  useEffect(() => { // set common zoom lvls to use in right map
    if (mapPlacement === 'right') {
      // setGeohashZoom(commonZoomLvls.geohash);
      setSiteZoom(commonZoomLvls.sites);
    }
  }, [commonZoomLvls]);

  // map permission
  const hasPermissionToWebMap = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user || user.web_map_access !== true) {
      return navigate("/");
    }
    //set site permissiom
    // if (user) {
    //   setEdotcoSiteAccess(user.edotco_site_access);
    //   setOperatorSiteAccess(user.operator_site_access);
    // }
  };

  const getBbox = useMemo(() => {
    if (!mapRef?.current) return { neBound: [], swBound: [] }
    const ne = mapRef.current.getBounds().getNorthEast();
    const sw = mapRef.current.getBounds().getSouthWest();
    const BT = boxTolerance[mapRef.current.getZoom()] || 0;
    return {
      neBound: [ne.lng() + BT, ne.lat() + BT],
      swBound: [sw.lng() - BT, sw.lat() - BT]
    }
  }, [boundsChanged, searchAreaBtn, eyeIcons.supply, eyeIcons.demand, eyeIcons.sites, eyeIcons.capacity]);

  // map related handlers
  const onMapIdle = () => {
    const newZoom = mapRef.current.getZoom();
    if (mapZoomLevel !== newZoom) setMapZoomLevel(newZoom);
    const ne = mapRef.current.getBounds().getNorthEast(); // new bounds
    const sw = mapRef.current.getBounds().getSouthWest(); // new bounds
    if (lastDataLoadedBound) {
      const { neBound: lastNe, swBound: lastSw } = lastDataLoadedBound; // last data loaded bound of any section (supply, demand, etc.)
      const lastBoxBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(
          lastSw[1],
          lastSw[0]
        ),
        new window.google.maps.LatLng(
          lastNe[1],
          lastNe[0]
        )
      );
      if (
        !lastBoxBounds.contains(
          new window.google.maps.LatLng(ne.lat(), ne.lng())
        ) ||
        !lastBoxBounds.contains(
          new window.google.maps.LatLng(sw.lat(), sw.lng())
        )
      ) {
        if (!appliedFilters.level && searchAreaBtn !== SEARCH_AREA_STATES.INIT && selectedCapacityGeohashes.length === 0) setSearchAreaBtn(SEARCH_AREA_STATES.B_C);
        else setBoundsChanged(boundsChanged + 1);
      }
    } else setBoundsChanged(boundsChanged + 1);
  };

  // when any section (supply, demand, etc.) loads data, store its data loaded bound
  const storeLastDataLoadedBounds = useCallback(() => {
    setLastDataLoadedBound(getBbox);
  }, [getBbox]);

  const handleOnLoad = (map) => {
    initMap(map);
  };

  const onChangeMapType = () => {
    if (mapRef.current) setMapTypeId(mapRef.current.getMapTypeId());
  };

  const handleBoundsChange = () => {
    // control right map
    if (mapRef.current && updateZoom) updateZoom(mapRef.current.getZoom());
    if (mapRef.current && updateCenter) updateCenter(mapRef.current.getCenter());
  }

  const handleMapClick = (e) => {
    if (mapTool === MapTools.DOWNLOAD_PIN) {
      setToolMarkers([{
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      }]);
    } else if (mapTool) {
      if (
        mapTool === MapTools.DOWNLOAD_POLYGON &&
        mapZoomLevel < preferredDownloadZoom
      ) {
        toast.error(
          "Your current zoom level does not support drawing polygons on the map",
          { theme: "colored" }
        );
        return;
      }
      setToolMarkers([
        ...toolMarkers,
        { lat: e.latLng.lat(), lng: e.latLng.lng() },
      ]);
    }
  };

  /**
 * Reset follwing states
 */
  useEffect(() => {
    const resetAllStates = async () => {
      setPermittedOpsForSupply(() => []);
      setPermittedBestOpsForSupply(() => []);
      setCapacityLegend(() => []);
      setSelectedCountryPremssions(() => []);
      await new Promise(resolve => setTimeout(resolve, 0));
    };
    resetAllStates();
  }, [appliedFilters.country])

  /**
   * Reset Operators Details
   */
  const setOperatorsDetails = async (operators) => {
    setOperatorDetails([]);
    await new Promise(resolve => setTimeout(resolve, 0));
    setOperatorDetails(operators);
  };

  // config based handlers
  // operators - based on config
  const fetchOperators = async (filters) => {
    try {
      const permittedOpCodes = permissions?.find((perm) => perm.country === filters?.countryCode);
      const { data } = await getOperatorsByQuarter(filters);
      const anonmizedOpsList = await getAnonymizedOperatorsByCountry(filters?.countryCode);
      setSelectedCountryPremssions(permittedOpCodes?.permissions);

      if (data) {
        const { operators = [], supplyStrengthLevels: sl = [], demandStrengthLevels: dsl = [], zoomLevels, siteLegend, 
          supplyLegend_5G, supplyLegend_4G, supplyLegend_3G, demandLegend, capacityLegend, 
          msLegend_5G, msLegend_4G, msLegend_3G, tableColorConfigs, ibsLegend_5G, ibsLegend_4G, 
          ibsLegend_3G, ibsLegend_2G, quadLabels } = data;
        let keyWord = data?.operators ? 'operators' : '';

        await setOperatorsDetails(data?.operators);
        [
          Sections.CAPACITY,
          Sections.OP_SITES,
          Sections.SUPPLY_OPERATOR_PERFORMANCE,
          Sections.SUPPLY_BEST_OPERATOR,
          Sections.SITE_ECO_EXISTING_TENANTS,
          Sections.SITE_ECO_PROPOSED_TENANTS,
          Sections.BEST_IBS,
          Sections.IBS,
          Sections.OP_MARKET_SHARE,
          Sections.TOP_MARKET_SHARE
        ]?.forEach((section) => setPermissionsBySection(section, data?.operators, anonmizedOpsList));
  
        setOperatorKeyWord(keyWord ? keyWord.charAt(0).toUpperCase() + keyWord.slice(1) : 'Operators');
  
        setDefaultZoomLevels(zoomLevels || []);
        setSiteLegend(siteLegend);
        setSupplyLegend5G(supplyLegend_5G);
        setSupplyLegend4G(supplyLegend_4G);
        setSupplyLegend3G(supplyLegend_3G);
        setDemandLegend(demandLegend);
        setCapacityLegend(capacityLegend);
        setCapacityChartConfig(capacityLegend);
        setMsConfigType({ "5G": msLegend_5G, "4G": msLegend_4G, "3G": msLegend_3G });
  
        let msConfigTypes = [msLegend_3G, msLegend_4G, msLegend_5G].find(legend => legend?.length !== 0) || [];
        setMsConfig(msConfigTypes);
        setMsTableColorConfig(tableColorConfigs);
  
        setIBSConfig({
          "5G": ibsLegend_5G,
          "4G": ibsLegend_4G,
          "3G": ibsLegend_3G,
          "2G": ibsLegend_2G
        });
        setQuadLabelsConfig(quadLabels);

        const lvl = {};
        sl.forEach((x) => (lvl[formatSupplyLevel(x.level)] = x.color));
        setSupplyStrengthLevels(lvl);

        const demandLvl = {};
        dsl.forEach((x) => (demandLvl[x.level] = x.color));
        setDemandFilters((prev) => ({ ...prev, strengthLevels: demandLvl }));

      }
    } catch (err) {
      console.log(err);
      setOperatorDetails([]);
    }
  };

  const checkAnonmyzedValues = async (opsArray, countryCode) => {
    const anonmizedOpsList = await getAnonymizedOperatorsByCountry(countryCode);
    const anonymizedArray = opsArray.filter(level => level.is_permitted === false && level.is_others === false && level.operator !== 'Others');
    const anonymizedOps = anonymizedArray?.map(op => {
      const anonymizedEntry = anonmizedOpsList.find(anon => anon.operator === op.operator);
      if (anonymizedEntry) {
        return {
          ...op,
          operator: anonymizedEntry.operator,
          name: anonymizedEntry.anonymizedName,
          isAnonymized: true,
        };
      }
      return {
        ...op,
        isAnonymized: false,
      };
    });
    const nonAnonymizedOps = opsArray.filter(op => 
      !anonymizedOps.some(anonOp => anonOp.operator === op.operator)
    );

    const processedNonAnonymizedOps = nonAnonymizedOps.map((op) => ({
      ...op,
      isAnonymized: false,
    }));
    return [...processedNonAnonymizedOps, ...anonymizedOps];
  };
  
  const setPermissionsBySection = async (
    section,
    operators,
    anonmizedOpsList
  ) => {
    // Define section-specific configurations
    const sectionConfig = {
      [Sections.CAPACITY]: {
        filterParams: [appliedFilters.countryCode, Names_For_Filter_OPs.CAPACITY, null, null, null],
        setState: (filteredOperators) => {
          setCapacityFilterData((prev) => ({ ...prev, operators: filteredOperators }));
          setPermittedOpsForCapacity(filteredOperators);
        },
      },
      [Sections.OP_SITES]: {
        filterParams: [
          appliedFilters.countryCode,
          Names_For_Filter_OPs.SITES,
          Names_For_Filter_OPs.OPERATOR_SITES,
          null,
          null,
        ],
        setState: (filteredOperators) => {
          setSelectedOperatorSiteOperator(filteredOperators[0]?.operator);
          setPermittedOpsForOpSite(filteredOperators);
        },
      },
      [Sections.SUPPLY_OPERATOR_PERFORMANCE]: {
        filterParams: [
          appliedFilters.countryCode,
          Names_For_Filter_OPs.SUPPLY,
          Names_For_Filter_OPs.SUPPLY_OPERATOR_PERFORMANCE,
          null,
          null,
        ],
        setState: (filteredOperators) => {          
          setSupplyFilters((prev) => ({ ...prev, operator: filteredOperators[0]?.operator }));
          setPermittedOpsForSupply(filteredOperators);
        },
      },
      [Sections.SUPPLY_BEST_OPERATOR]: {
        filterParams: [
          appliedFilters.countryCode,
          Names_For_Filter_OPs.SUPPLY,
          Names_For_Filter_OPs.SUPPLY_BEST_OPERATOR,
          null,
          null,
        ],
        setState: setPermittedBestOpsForSupply,
      },
      [Sections.SITE_ECO_EXISTING_TENANTS]: {
        filterParams: [
          appliedFilters.countryCode,
          Names_For_Filter_OPs.SITES,
          Names_For_Filter_OPs.EC_SITES,
          Names_For_Filter_OPs.EXISTING_TENANTS,
          null,
        ],
        postProcess: async (operatorsList) => {
          const checkedOps = await checkAnonmyzedValues(operatorsList, appliedFilters.countryCode);
          setPermittedOpsForECSiteET(checkedOps);
          const mnoOperators = checkedOps.filter(
            (op) => op.type === "MNO" && !op.is_others && op.operator !== "Others"
          );
          if (mnoOperators.length) {
            setSelectedEdotcoSiteOperator({ operatorList: [mnoOperators[0]?.operator], value: null });
          }
        },
      },
      [Sections.SITE_ECO_PROPOSED_TENANTS]: {
        filterParams: [
          appliedFilters.countryCode,
          Names_For_Filter_OPs.SITES,
          Names_For_Filter_OPs.EC_SITES,
          Names_For_Filter_OPs.PROPOSED_TENANTS,
          null,
        ],
        postProcess: async (operatorsList) => {
          const checkedOps = await checkAnonmyzedValues(operatorsList, appliedFilters.countryCode);
          setPermittedOpsForECSitePT(checkedOps);
          const mnoOperators = checkedOps.filter(
            (op) => op.type === "MNO" && !op.is_others && op.operator !== "Others"
          );
          if (mnoOperators.length) {
            setSelectedProposedOperator({ operatorList: [mnoOperators[0]?.operator], value: null });
          }
        },
      },
      [Sections.BEST_IBS]: {
        filterParams: [
          appliedFilters.countryCode,
          Names_For_Filter_OPs.IBS,
          Names_For_Filter_OPs.IBS_BEST_OPERATOR,
          null,
          null,
        ],
        setState: setPermittedOpsForBestIBS,
      },
      [Sections.IBS]: {
        filterParams: [
          appliedFilters.countryCode,
          Names_For_Filter_OPs.IBS,
          Names_For_Filter_OPs.IBS_OPERATOR_PERFORMANCE,
          null,
          null,
        ],
        setState: setPermittedOpsForIBS,
      },
      [Sections.OP_MARKET_SHARE]: {
        filterParams: [
          appliedFilters.countryCode,
          Names_For_Filter_OPs.MARKET_SHARE,
          Names_For_Filter_OPs.OP_MARKET_SHARE,
          null,
          null,
        ],
        setState: setPermittedOpsForMS,
      },
      [Sections.TOP_MARKET_SHARE]: {
        filterParams: [
          appliedFilters.countryCode,
          Names_For_Filter_OPs.MARKET_SHARE,
          Names_For_Filter_OPs.TOP_MARKET_SHARE,
          null,
          null,
        ],
        setState: setPermittedOpsForTopMS,
      },
    };
  
    // Get the configuration for the given section
    const config = sectionConfig[section];
    if (!config) return;
  
    // Fetch permitted operators
    const permitedOperators = await filterOpPermissions(
      ...config.filterParams,
      operators,
      false,
      anonmizedOpsList
    );
    // If post-processing is needed
    if (config?.postProcess) {
      await config.postProcess(permitedOperators);
    } else {
      // Otherwise, filter and set state
      const filteredOperators = permitedOperators?.filter((op) => op.type === "MNO");
      config.setState(filteredOperators);
    }
  };

  const filterOpPermissions = async (countryCode, level1, level2, level3, level4, opData, isDownloadModule = false, anonmizedOpsList = []) => {
    const operatorsArray = opData?.length ? opData : operatorDetails;
    const permissionsByCountry = permissions?.find((perm) => perm.country === countryCode)

    if (operatorsArray?.length) {
      if(!anonmizedOpsList?.length){
       anonmizedOpsList = await getAnonymizedOperatorsByCountry(countryCode)
      }
      const findPermissionByName = (permissions, name) => {
        return permissions.find((permission) => permission.name === name) || null;
      };
      let currentPermissions = permissionsByCountry.permissions;


      // Filter by level1
      if (level1) {
        const level1Permission = findPermissionByName(currentPermissions, level1);
        if (!level1Permission) {
          console.error(`Level 1 "${level1}" not found.`);
          return [];
        }
        currentPermissions = level1Permission.sub_permissions;
        if (level1Permission?.operators && level1Permission?.operators?.length) {
          const permitedOps = operatorsArray?.filter(op =>
            level1Permission.operators?.some(level => level.operatorCode === op.operator && level.is_permitted === true)
          );
          const anonymizedArray = operatorsArray?.filter(op =>
            level1Permission.operators?.some(level => level.operatorCode === op.operator && level.is_permitted === false && level.operatorCode !== 'Others')
          );
          const anonymizedOps = anonymizedArray?.map(op => {
            const anonymizedEntry = anonmizedOpsList.find(anon => anon.operator === op.operator);

            if (anonymizedEntry) {
              return {
                ...op,
                operator: anonymizedEntry.operator,
                name: anonymizedEntry.anonymizedName,
                isAnonymized: true
              };
            }
            return op;
          });
          const ops = [
            ...permitedOps.map(item => ({ ...item, isAnonymized: false })),
            ...anonymizedOps
          ];
          return ops;
        }
      }

      // Filter by level2
      if (level2) {
        const level2Permission = findPermissionByName(currentPermissions, level2);
        if (!level2Permission) {
          console.error(`Level 2 "${level2}" not found.`);
          return [];
        }
        currentPermissions = level2Permission.sub_permissions;
        if (level2Permission?.operators && level2Permission?.operators?.length) {
          const permitedOps = operatorsArray?.filter(op =>
            level2Permission.operators?.some(level => level.operatorCode === op.operator && level.is_permitted === true)
          );
          const anonymizedArray = operatorsArray?.filter(op =>
            level2Permission.operators?.some(level => level.operatorCode === op.operator && level.is_permitted === false && level.operatorCode !== 'Others')
          );
          const anonymizedOps = anonymizedArray?.map(op => {
            const anonymizedEntry = anonmizedOpsList.find(anon => anon.operator === op.operator);

            if (anonymizedEntry) {
              return {
                ...op,
                operator: anonymizedEntry.operator,
                name: anonymizedEntry.anonymizedName,
                isAnonymized: true
              };
            }
            return op;
          });
          const ops = [
            ...permitedOps.map(item => ({ ...item, isAnonymized: false })),
            ...anonymizedOps
          ];
          return ops;
        }
      }

      // Filter by level3
      if (level3) {
        const level3Permission = findPermissionByName(currentPermissions, level3);
        if (!level3Permission) {
          console.error(`Level 3 "${level3}" not found.`);
          return [];
        }
        currentPermissions = level3Permission.sub_permissions;
        if (level3Permission?.operators && level3Permission?.operators?.length) {
          if (level2 === "E.Co Sites") {
            if (isDownloadModule) {
              const permitedOps = operatorsArray?.filter(op =>
                level3Permission.operators?.some(level => level.operatorCode === op.operator && level.is_permitted === true)
              );

              const anonymizedArray = operatorsArray?.filter(op =>
                level3Permission.operators?.some(level => level.operatorCode === op.operator && level.is_permitted === false && level.operatorCode !== 'Others')
              );
              const anonymizedOps = anonymizedArray?.map(op => {
                const anonymizedEntry = anonmizedOpsList.find(anon => anon.operator === op.operator);

                if (anonymizedEntry) {
                  return {
                    ...op,
                    operator: anonymizedEntry.operator,
                    name: anonymizedEntry.anonymizedName,
                    isAnonymized: true
                  };
                }
                return op;
              });

              const ops = [
                ...permitedOps.map(item => ({ ...item, isAnonymized: false })),
                ...anonymizedOps
              ];
              return ops;
            }
            const permitedOps = operatorsArray
              ?.filter(op =>
                level3Permission.operators?.some(level => level.operatorCode === op.operator)
              )
              .map(op => {
                const matchingOperator = level3Permission.operators?.find(level => level.operatorCode === op.operator);
                return {
                  ...op,
                  is_permitted: matchingOperator?.is_permitted,
                  is_others: matchingOperator?.is_others,
                };
              });
            return permitedOps;
          }
          else {
            const permitedOps = operatorsArray?.filter(op =>
              level3Permission.operators?.some(level => level.operatorCode === op.operator && level.is_permitted === true)
            );
            
            const anonymizedArray = operatorsArray?.filter(op =>
              level3Permission.operators?.some(level => level.operatorCode === op.operator && level.is_permitted === false && level.operatorCode !== 'Others')
            );
            const anonymizedOps = anonymizedArray?.map(op => {
              const anonymizedEntry = anonmizedOpsList.find(anon => anon.operator === op.operator);

              if (anonymizedEntry) {
                return {
                  ...op,
                  operator: anonymizedEntry.operator,
                  name: anonymizedEntry.anonymizedName,
                  isAnonymized: true
                };
              }
              return op;
            });
            const ops = [
              ...permitedOps.map(item => ({ ...item, isAnonymized: false })),
              ...anonymizedOps
            ];
            return ops;
          }
        }
      }

      // Filter by level4
      if (level4) {
        const level4Permission = findPermissionByName(currentPermissions, level4);
        if (!level4Permission) {
          console.error(`Level 4 "${level4}" not found.`);
          return [];
        }
        if (level4Permission.operators && level4Permission?.operators?.length) {
          const permitedOps = operatorsArray?.filter(op =>
            level4Permission.operators?.some(level => level.operatorCode === op.operator && level.is_permitted === true)
          );
          const anonymizedArray = operatorsArray?.filter(op =>
            level4Permission.operators?.some(level => level.operatorCode === op.operator && level.is_permitted === false && level.operatorCode !== 'Others')
          );
          const anonymizedOps = anonymizedArray?.map(op => {
            const anonymizedEntry = anonmizedOpsList.find(anon => anon.operator === op.operator);

            if (anonymizedEntry) {
              return {
                ...op,
                operator: anonymizedEntry.operator,
                name: anonymizedEntry.anonymizedName,
                isAnonymized: true
              };
            }
            return op;
          });
          const ops = [
            ...permitedOps.map(item => ({ ...item, isAnonymized: false })),
            ...anonymizedOps
          ];
          return ops;
        }
      }

      console.error("No operators found for the specified levels.");
      return [];
    }
    return []
  };
  const fetchGlobalConfig = async (countryCode) => {
    const result = await getMapGlobalConfig(countryCode);
    setGlobalConfig(result?.data);
  }

  const onChangeFocusedAdminLvl = (focusedAdminLevel) => {
    const { country, level, levelVal } = focusedAdminLevel;
    const {
      country: currentCountry,
      level: currentLevel,
      levelVal: currentLevelVal
    } = appliedFilters;
    setAppliedFilters(focusedAdminLevel); // set county/date/levels
    if (setCommonLocationLevels) setCommonLocationLevels(focusedAdminLevel); // update parent from left only
    if (country && level && levelVal && (currentLevelVal !== levelVal || currentLevel !== level)) {
      getPolygon(focusedAdminLevel);
    } else {
      if (country && level == null) {
        // if (!currentLevel || !(eyeIcons.supply || eyeIcons.demand || eyeIcons.capacity)) {
          if (mapPlacement === 'left' && currentCountry !== country) fitBoundsByAddress(country); // zoom to country
          // if (focusedPolygon.length) setFocusedPolygon([]);
          // if (areaGeohashPolygon.length) setAreaGeohashPolygon([]);
        // }
        if (mapPlacement === 'right') {
          // if (focusedPolygon.length) setFocusedPolygon([]);
          // if (areaGeohashPolygon.length) setAreaGeohashPolygon([]);
        }
      } else if (country === null && mapRef.current && mapPlacement === 'left') mapRef.current.setZoom(initMapSettings.zoom); // zoom to world map}
    }
  };

  const getPolygon = async (focusedAdminLevel, focus = true) => {
    try {
      if (focusedAdminLevel.level === 'geoHashLevel') return;
      const perPage = pageSizeByAdmLevels[`${focusedAdminLevel.countryCode}_${focusedAdminLevel.level}`] || 70;
      await paginatedApiInvoker(
        (pageSize, page) => getPolygonByAdminLevel(focusedAdminLevel, pageSize, page),
        perPage,
        () => setFocusedPolygon([]),
        (data) => {
          if (data) {
            const polygons = getPolygonFromApiResp(data);
            setFocusedPolygon(prevState => [...prevState, ...polygons]);
          }
        },
        (data) => {
          if (data) {
            const polygons = getPolygonFromApiResp(data);
            if (focus) fitPolygonBounds(polygons)
            if (focusedAdminLevel?.level === "adminLevel3") {
              const selectedPolygon = data.map(location => location?.polygon);
              setSelectedPolygons(selectedPolygon);
            } else if (focusedAdminLevel?.level === "adminLevel2" || focusedAdminLevel?.level === "adminLevel1") {
              setSelectedPolygons([]);
            }
          }
        }
      );
    } catch (err) {
      if (err?.response && err.response.data && err.response.data.errorMessage)
        toast.warn(err?.response?.data?.errorMessage, { theme: "colored" });
      console.log(err);
    }
  };

  const fitPolygonBounds = (polygons) => {
    if (mapRef.current) {
      const bounds = new window.google.maps.LatLngBounds();
      polygons.forEach((polygon) => {
        polygon.forEach((latLng) => bounds.extend(latLng));
      });
      mapRef.current.fitBounds(bounds);
    }
  };

  const fitBoundsByAddress = (address) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: address }, function (results, status) {
      if (status === window.google.maps.GeocoderStatus.OK) {
        mapRef.current.setCenter(results[0].geometry.location);
        mapRef.current.fitBounds(results[0].geometry.viewport);
      }
    });
  };

  const markOnMap = (address) => {
    setMarkerOnMap(address);
    mapRef.current.setCenter(address);
  };

  // location search handlers
  const onClickAdminLvlEyeIcon = (isClicked = false) => {
    setEyeIcons({ ...eyeIcons, adminLevel: isClicked });
  };

  const getAddressBounds = async () => {
    let bounds;
    if (appliedFilters.level) {
      bounds = new window.google.maps.LatLngBounds();
      (appliedFilters.level === "geoHashLevel" ? [areaGeohashPolygon] : focusedPolygon).forEach((polygon) => {
        polygon.forEach((latLng) => bounds.extend(latLng));
      });
    } else if (appliedFilters.country) {
      const geocoder = new window.google.maps.Geocoder();
      bounds = await new Promise((resolve, reject) => {
        geocoder.geocode(
          { address: appliedFilters.country },
          function (results, status) {
            if (status === window.google.maps.GeocoderStatus.OK) {
              resolve(results[0].geometry.bounds);
            } else
              reject(status);
          });
      });
    }
    return bounds;
  }

  const evaluateAndMarkSearchInput = async ({ type, val }) => {
    let errMsg = "";
    let returnVal = true;
    const border = appliedFilters.level === "geoHashLevel" ? "Geohash" : appliedFilters.level ? "Administrative Level" : "Country";
    if (type === 'point') { // lat,lng or address search
      const bounds = await getAddressBounds();
      if (bounds.contains(val)) {
        markOnMap(val);
        mapRef.current.setZoom(15);
      } else {
        errMsg = `Searched input is located outside from the selected ${border}`;
        setMarkerOnMap(null);
      }
    } else if (type === 'geohash') {
      const geoPoly = convertGeohashToPolygon(val);
      const bounds = await getAddressBounds();
      if (geoPoly.every(latLng => bounds.contains(latLng))) {
        setGeohashOnMap(geoPoly);
        fitPolygonBounds([geoPoly]);
      } else {
        errMsg = `Searched input is located outside from the selected ${border}`;
        setGeohashOnMap(null);
      }
    } else if (type === 'building') {
      const { countryCode, quarter, year, level, levelVal } = appliedFilters
      const buildingResponse = await fetchBuildingLocatorSearch({
        country: countryCode,
        quarter: `${quarter} ${year}`,
        ...(level && {
          level:
            level === "adminLevel1"
              ? "adm1"
              : level === "adminLevel2"
                ? "adm2"
                : "adm3",
        }),
        ...(levelVal && { levelVal }),
        searchVal: val
      })
      if (buildingResponse?.data?.geoPoint) {
        const geoVal = {
          lat: buildingResponse?.data?.geoPoint.coordinates[1],
          lng: buildingResponse?.data?.geoPoint.coordinates[0]
        }
        const bounds = await getAddressBounds();
        if (bounds.contains(geoVal)) {
          markOnMap(geoVal);
          mapRef.current.setZoom(15);
        }
      } else {
        setMarkerOnMap(null);
        returnVal = false
      }
    }
    if (errMsg.length) {
      toast.error(
        errMsg,
        { theme: "colored" })
    }
    return returnVal;
  };

  const isAddressOrGeohash = (val) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: val }, async function (results, status) {
      const bounds = await getAddressBounds();
      const location = {
        lat: results?.[0]?.geometry?.location?.lat(),
        lng: results?.[0]?.geometry?.location?.lng()
      }
      if (status === window.google.maps.GeocoderStatus.OK && bounds.contains(location)) { // valid address
        evaluateAndMarkSearchInput({
          type: "point", val: {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          }
        });
      } else {
        const isBuildingAvailable = await evaluateAndMarkSearchInput({ type: "building", val: val });
        if (!isBuildingAvailable) {
          const tempGeohash = val.replace(/\s/g, "");
          if (tempGeohash.length >= 5) { // valid geohash
            evaluateAndMarkSearchInput({ type: "geohash", val: tempGeohash });
          } else {
            toast.error("Invalid search. Please try again", { theme: "colored" });
            setMarkerOnMap(null);
          }
        }
      }
    });
  };

  const handleSearchedPosition = (val) => {
    if (val) {
      if (checkForLatLong(val)) {
        const searchPosition = val.split(",");
        if (mapRef.current) {
          const searchedLatLng = {
            lat: Number(searchPosition[0]),
            lng: Number(searchPosition[1]),
          };
          evaluateAndMarkSearchInput({ type: "point", val: searchedLatLng });
        }
      } else {
        isAddressOrGeohash(val);
      }
    } else {
      setMarkerOnMap(null);
      setGeohashOnMap(null);
    }
  };

  // supply section
  const onClickSupplyEyeIcon = async (isClicked = false) => {
    setEyeIcons({ ...eyeIcons, supply: isClicked, demand: false, capacity: false, marketShare: false, ibs: false });
    if (!isClicked) setDemandSupplyLegendData([]);
    if (tooltipData) setTooltipData();
  };

  const getSupplyLvlFilters = async (type) => {
    try {
      const { countryCode, year, quarter } = appliedFilters;
      if (countryCode && year && quarter) {
        const { data } = await getFilters({
          countryCode: appliedFilters.countryCode,
          year: appliedFilters.year,
          quarter: appliedFilters.quarter,
          column: supplyLvlMapper[type],
          table: "operator_statistics",
          // hint: supplyLvlInfoMapper[supplyLvlMapper[type]] ?? null // commented because the perfomance issue
        }, {
          params: {
            filters: `connection_type:${supplyFilters.connectionType.custom},data_type:${supplyFilters.dataType.custom}${supplyFilters.aggrType.custom ? ",agg_type:"+supplyFilters.aggrType.custom : ""}`,
          }
        });
        if (data) {
          let filters = supplyCustOpFiltersMap
            .filter((x) => data.find((y) => y._id === x.key))
            .map((m) => ({ filterName: m.value, isChecked: true, key: m.key }));
  
          return [
            ...filters,
            { filterName: "No Data", isChecked: true, key: "No Data" },
          ];
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const onChangeSupplyType = (type) => {
    setSupplyFilters((prev) => ({ ...prev, supplyType: type }));
    if (tooltipData) setTooltipData();
  };

  const fetchAllSupplyLvlFilters = () => {
    const promiseArr = [];
    for (const type in SupplyType) {
      if(SupplyType[type]==="RSRP" || SupplyType[type]==="Download" || SupplyType[type]==="Upload"){
        promiseArr.push(getSupplyLvlFilters(SupplyType[type]));
      }
    }
    Promise.allSettled(promiseArr).then((results) => {
      setSupplyLvlFilters({
        [SupplyType.RSRP]: results[0].value,
        [SupplyType.DOWNLOAD]: results[1].value,
        [SupplyType.UPLOAD]: results[2].value,
        [SupplyType.DATA_RECEIVED]: [],
        [SupplyType.DATA_TRANSMIT]: [],
        [SupplyType.DATA_TRANSCEIVED]: [],
      });
    });

  };

  const onChangeSupplyBestOpToggle = (isBest) => {
    setSupplyFilters((prev) => ({ ...prev, isBestOperator: isBest }));
    if (eyeIcons.supply && tooltipData) setTooltipData();
  };

  const onChangeCustomOpSupply = (operator) => {
    const selectedOp = operatorsPerfPermittedSupply.find(op => op.operator === operator);
    const anom = {
      isAnonymized: selectedOp?.isAnonymized,
      name: selectedOp?.name
    }
    setSupplyFilters((prev) => ({ ...prev, operator, anom }));
    if (eyeIcons.supply && tooltipData) setTooltipData();
  };

  const generateSupplyLegend = async (isBestOperator, isSupplyVisible) => {
    if (!isSupplyVisible) return;
  
    if (isBestOperator) {
      setDemandSupplyLegendData([
        {
          title: operatorKeyWord,
          values: bestOperatorsPermitted
            ?.filter(op => op.type === "MNO" && op.isAnonymized === false)
            .map(op => ({
              markerType: "colorBox",
              markerColor: op.color,
              img: op.icon,
            })),
          notPermitted: { isNotPermitted: true, text: "Not Permitted" },
        },
      ]);
    } else {
      let supplyDataArray = [];
  
      // Select supply legend based on connection type
      if (supplyFilters.connectionType.custom === connectionTypes["5G"]) {
        supplyDataArray = supplyLegend5G;
      } else if (supplyFilters.connectionType.custom === connectionTypes["4G"]) {
        supplyDataArray = supplyLegend4G;
      } else {
        supplyDataArray = supplyLegend3G;
      }

      let supplyColorValuesArr = [];
  
      supplyDataArray.forEach(item => {
        item?.content?.forEach(contItem => {
          if ([SupplyType.RSRP, SupplyType.DOWNLOAD, SupplyType.UPLOAD].includes(supplyFilters.supplyType)) {
            if (contItem.type === supplyFilters.supplyType) {
              contItem.values?.forEach(obj => {
                supplyColorValuesArr.push({
                  text: obj?.text,
                  markerType: obj?.colorType,
                  markerColor: obj?.color,
                });
              });
              setDemandSupplyLegendData([
                {
                  title:contItem?.title,
                  values: supplyColorValuesArr,
                },
              ]);
            }
          } else if (
            [SupplyType.DATA_RECEIVED, SupplyType.DATA_TRANSCEIVED, SupplyType.DATA_TRANSMIT].includes(
              supplyFilters.supplyType
            )
          ) {
            const gradientArr = contItem.values[0]?.colorGradient || defaultGradientArr;

            const min = parseInt(minMaxSupplyOp[supplyFilters.supplyType]?.min) || 0;
            const max = parseInt(minMaxSupplyOp[supplyFilters.supplyType]?.max) || 0;

            const title = supplyFilters.supplyType.replace(/_/g, " "); 
  
            setDemandSupplyLegendData([
              {
                title,
                gradient: {
                  colors: gradientArr,
                  min,
                  max,
                },
              },
            ]);
          }
        });
      });
    }
  };
  

  const onChangeLevelFilterToggle = ({ filterName, key, isChecked }) => {
    const filterSection = supplyLvlFilters[supplyFilters.supplyType] || [];
    const updatedFilterIdx = filterSection.findIndex((x) => x.key === key);
    if (updatedFilterIdx > -1) {
      filterSection[updatedFilterIdx] = {
        filterName,
        key,
        isChecked: !isChecked,
      };
      setSupplyLvlFilters((prev) => ({
        ...prev,
        [supplyFilters.supplyType]: filterSection,
      }));
    };
    if (tooltipData) setTooltipData();
  };

  const onChangeSupplyConnectionType = (val) => {
    if (tooltipData && eyeIcons.supply) setTooltipData();
    if (
      supplyFilters.supplyType === SupplyType.DATA_RECEIVED ||
      supplyFilters.supplyType === SupplyType.DATA_TRANSCEIVED ||
      supplyFilters.supplyType === SupplyType.DATA_TRANSMIT
    ) {
      setSupplyFilters((prev) => ({
        ...prev,
        connectionType: val,
        aggrType: { best: null, custom: null },
      }));
      return;
    }
    setSupplyFilters({ ...supplyFilters, connectionType: val });
  };

  const onChangeSupplyDataType = (val) => {
    if (tooltipData && eyeIcons.supply) setTooltipData();
    setSupplyFilters((prev) => ({ ...prev, dataType: val }));
  };

  const resetSupplyToolTip = () => {
    if (tooltipData && eyeIcons.supply) setTooltipData();
  };

  // demand section
  const onClickDemandEyeIcon = (isClicked = false) => {
    setEyeIcons({ ...eyeIcons, demand: isClicked, supply: false, capacity: false, marketShare: false, ibs: false });

    if (!isClicked) setDemandSupplyLegendData([]);
    if (tooltipData) setTooltipData();
  };

  const onChangeDemandScore = (score) => {
    setDemandFilters({ ...demandFilters, demandType: score });
    if (eyeIcons.demand && tooltipData) setTooltipData();
  };

  const onChangeDemandOperator = (operator) => {
    setDemandFilters((prevState) => ({ ...prevState, operator}));
    if (eyeIcons.demand && tooltipData) setTooltipData();
  };

  const onChangeDemandNoDataToggle = (checked) => {
    setDemandFilters({ ...demandFilters, noData: !checked });
    setTooltipData(null);
  };

  const onChangeDemandScoreRange = (range) => {
    setDemandFilters({ ...demandFilters, range });
    if (eyeIcons.demand && tooltipData) setTooltipData();
  };

  const generateDemandLegend = (score, isDemandVisible) => {
    if (isDemandVisible) {
      let demandLegendGradientArr;
      let minPos = Number.POSITIVE_INFINITY;
      let maxPos = Number.NEGATIVE_INFINITY;
      let colorValues = [];
      demandLegend.forEach((item) => {
        item?.content.forEach((itemObj) => {
          if (score === itemObj?.type && score !== DemandScoreTypes.AFFLUENCE) {
            itemObj?.values.forEach((colorObj) => {
              demandLegendGradientArr = colorObj?.colorGradient?.map((obj) => {
                const { _id, ...rest } = obj
                const pos = obj.pos;
                if (pos < minPos) {
                  minPos = pos;
                }
                if (pos > maxPos) {
                  maxPos = pos;
                }
                return rest;
              })
            })
            setDemandSupplyLegendData([
              {
                title: itemObj?.title,
                gradient: { colors: demandLegendGradientArr, min: parseInt(minPos), max: parseInt(maxPos) },
              },
            ]);
          } else if (score === itemObj?.type && score === DemandScoreTypes.AFFLUENCE){
              itemObj.values.forEach((obj) => {
                let colorObj = {};
                colorObj.text = obj?.text
                colorObj.markerType = obj?.colorType
                colorObj.markerColor = obj?.color
                colorValues.push(colorObj)
              })
              setDemandSupplyLegendData([
                {
                  title: itemObj?.title,
                  values: colorValues
                },
              ]);
          }
        })
      })
    }
  };

  const onChangeDemandConnectionType = (val) => {
    if (tooltipData && eyeIcons.demand) setTooltipData();
    setDemandFilters({ ...demandFilters, connectionType: val });
  };

  const onChangeDemandDataType = (val) => {
    if (tooltipData && eyeIcons.demand) setTooltipData();
    setDemandFilters({ ...demandFilters, dataType: val });
  };

  // sites section
  const onClickSiteEyeIcon = async (isClicked = false) => {
    setEyeIcons({ ...eyeIcons, sites: isClicked });
    if (!isClicked) {
      setEdotcoSiteLegendData([]);
      setOperatorSiteLegendData([]);
    }
  };

  const onClickMarketShareEyeIcon = async (isClicked = false) => {
    setEyeIcons({ ...eyeIcons, marketShare: isClicked, supply: false, demand: false, capacity: false, ibs: false });
    if (tooltipData) setTooltipData();
  };

  const onClickIbsEyeIcon = async (isClicked = false) => {
    setEyeIcons({ ...eyeIcons, ibs: isClicked, supply: false, demand: false, capacity: false, marketShare: false });
    setIBSVisible(isClicked)
    if (tooltipData) setTooltipData();
  };

  const generateSiteLegend = (
    isSitesVisible,
    isOperatorSiteVisible,
    isEdotcoSiteVisible
  ) => {
    if (isSitesVisible && operatorDetails) {

      setedotcoSiteVisible(isEdotcoSiteVisible);
      setOperatorSiteVisible(isOperatorSiteVisible);
      if (isOperatorSiteVisible) {
        let formattedOperatorSiteValues = operatorsPermittedOpSite.map((item) => {
          return {
            text: item.name,
            svg: <OperatorSiteIconLegend color={!item?.isAnonymized ? item.color : '#707070'} />,
          };
        });
        let operatorSiteLegend = {
          title: "Operator Sites",
          values: [...formattedOperatorSiteValues],
        };
        setOperatorSiteLegendData([operatorSiteLegend]);
      }
      if (!isOperatorSiteVisible) {
        setOperatorSiteLegendData([]);
      }
      if (isEdotcoSiteVisible) {
        // Set Permitted operators and set other category if exists others
        const mnoOperatorsET = operatorsPermittedECSiteET?.filter(
          operator => operator.is_others === false && operator.operator !== "Others" && operator.is_permitted === true
        );
        const mnoOperatorsETOthers = operatorsPermittedECSiteET?.filter(
          operator => operator.is_others === true
        );
        let updatedOperatorDetails = [];
        if (mnoOperatorsET?.length > 0) {
          updatedOperatorDetails = operatorDetails?.filter(op =>
            mnoOperatorsET?.some(mno => mno.operator === op.operator)
          );
        }
        const OperatorsETNotPermitted = operatorsPermittedECSiteET?.filter(
          operator => operator.is_permitted === false && operator.operatorCode !== 'Others' && operator.is_others === false
        );
        let formattedEdotcoSiteValues = updatedOperatorDetails.map((item) => {
          return {
            text: item?.name,
            svg: <EdotcoSiteIconLegend color={item?.color} />,
          };
        });

        let edotcoSiteLegend = []
        let edotcoSiteImgContent = []
        let edotcoSiteStatusImgContent = []
        edotcoSiteImgContent.push(...formattedEdotcoSiteValues)
        if (OperatorsETNotPermitted?.length && siteLegend[0]?.content[0]?.type === 'sites') {
          const notPermittedOp = {
            text: 'Not Permitted',
            imgType: 'svg',
            imgContent: '#707070',
            imgWidth: ''
          };
          const values = siteLegend[0]?.content[0]?.values;
          const exists = values?.some(
            (item) => item.text === notPermittedOp.text && item.imgContent === notPermittedOp.imgContent
          );
        
          if (!exists) {
            values?.push(notPermittedOp);
          }
        }
        siteLegend.forEach((item) => {

          item?.content.forEach((content) => {
            let imgContent = content?.values
            let type = content?.type
            let edotcoSiteObj = {}

            imgContent.forEach((img) => {
              let imgObj = {}
              if (img.imgType === 'svg') {
                imgObj.text = img?.text;
                imgObj.svg = <EdotcoSiteIconLegend color={img?.imgContent} />;
              } else {
                imgObj.text = img?.text;
                imgObj.markerType = img?.imgType;
                imgObj.markerImg = img?.imgContent;
                imgObj.imgWidth = img?.imgWidth;
              }

              if (type === 'status') {
                edotcoSiteStatusImgContent.push(imgObj)
              } else {
                if (img?.text === 'Others' && !mnoOperatorsETOthers?.length) {
                  return;
                }
                edotcoSiteImgContent.push(imgObj)
              }
            })

            edotcoSiteObj.title = content?.title;
            edotcoSiteObj.values = (type === 'status') ? edotcoSiteStatusImgContent : edotcoSiteImgContent;
            edotcoSiteLegend.push(edotcoSiteObj)
          })
        })

        setEdotcoSiteLegendData(edotcoSiteLegend);
      }
      if (!isEdotcoSiteVisible) {
        setEdotcoSiteLegendData([]);
      }
    } else {
      setEdotcoSiteLegendData([]);
      setOperatorSiteLegendData([]);
      setedotcoSiteVisible(false);
      setOperatorSiteVisible(false);
    }
  };

  // capacity section
  const loadCapacityBoundChange = () => {
    if (common_capa_cond) {
      if (!capacityFilters.selectedCapacitySubLvl) {
        onChangeCapacitySubFilters()
      } else {
        setCapacityGeohashes([])
        fetchCapacityData();
        generateCapacityLegend(eyeIcons.capacity);
        storeLastDataLoadedBounds(); // save data loaded bounds
      }
    }
    if (selectedCapacityAdvanceData.length) {
      generateCapacityLegend(eyeIcons.capacity)
    }
  }

  const common_capa_cond = useMemo(() => {
    return eyeIcons.capacity && geohashZoom && mapZoomLevel >= geohashZoom &&
      capacityFilters.selectedYear && capacityFilters.selectedMonth && capacityFilters.selectedOprtr && capacityFilters.selectedCapacityLvl === 'operator' &&
      selectedCapacityAdvanceData.length === 0 && capacityAdvanceDisable
    // && !(appliedFilters.level && appliedFilters.level !== 'geoHashLevel' && capacityGeohashes.length > 0);
  }, [eyeIcons.capacity, capacityFilters, mapZoomLevel, geohashZoom, selectedCapacityAdvanceData.length, capacityAdvanceDisable, appliedFilters.level, capacityGeohashes.length]);

  const fetchCapacityData = async () => {
    setCapacityGeohashes([]);
    if ((capacityFilters.selectedCapacitySubLvl === 'flag' && !capacityFilters.selectedFlag.length) ||
      (capacityFilters.selectedCapacitySubLvl === 'action' && !capacityFilters.selectedAction.length)
    ) {
      return;
    }
    const allFilters = getCommonCapacityFilters()
    let transformedArr = [];
    try {
      await paginatedApiInvoker(
        (pageSize, page) =>
          getCapacityGeohashes(allFilters, false, null, pageSize, page),
        30000, // perPage
        () => setApisOngoing(count => count + 1),
        (data) => {
          if (data) {
            if (
              capacityFilters.selectedCapacityLvl === "enodbID" ||
              capacityFilters.selectedCapacityLvl === "secID"
            ) {
              data.flatMap((obj) => {
                if (obj?.flag && obj?.action) {
                  obj?.geohashes?.map((geohash) => {
                    const polygon = convertGeohashToPolygon(geohash.g)
                    transformedArr.push({
                      geohash: geohash.g,
                      action: obj.action,
                      flag: obj.flag,
                      month: obj.month,
                      secID: obj.secID,
                      enodbID: obj.enodbID,
                      operator: obj.operator,
                      nwCoordinate: polygon[0],
                      seCoordinate: polygon[3]
                    });
                  });
                }
              })
              transformedArr = removeDuplicateGeoHashes(transformedArr)

              if (appliedFilters.level === 'geoHashLevel') {
                transformedArr = transformedArr.filter((item) => item.geohash.startsWith(appliedFilters.levelVal));
              }
              setSelectedCapacityGeohashes(transformedArr)
            } else {
              transformedArr = data.flatMap((obj) =>
                obj.geohashes.map((geohash) => ({
                  geohash: geohash.g,
                  action: obj.action,
                  flag: obj.flag,
                  month: obj.month,
                  secID: obj.secID,
                  enodbID: obj.enodbID,
                  operator: obj.operator,
                }))
              )
              transformedArr = removeDuplicateGeoHashes(transformedArr)

              if (appliedFilters.level === 'geoHashLevel') {
                transformedArr = transformedArr.filter((item) => item.geohash.startsWith(appliedFilters.levelVal));
              }
              setCapacityGeohashes(transformedArr)
            }
          }
        },
        () => {
          //Saving filters for download
          setDownloadData({
            ...downloadData,
            capacity: { ...allFilters, cancelToken: false },
          });
          if (!appliedFilters.level) setSearchAreaBtn(SEARCH_AREA_STATES.ENABLED);
          setApisOngoing(count => count - 1);
        }
      );
    } catch (err) {
      if (err?.response && err.response.data && err.response.data.errorMessage)
        toast.warn(err?.response?.data?.errorMessage, { theme: "colored" });
      console.log(err);
    }
  };

  const fetchCapacityChartData = async () => {
    const allFilters = getCommonCapacityFilters(false, true, false, true);
    if (allFilters.flag) delete allFilters.flag;
    if (allFilters.action) delete allFilters.action;
    const chartResponse = await getCapacityChartData(allFilters);

    if (chartResponse.data.length === 0) {
      setCapacityChartData({ xData: [`${capacityFilters.selectedMonth} ${capacityFilters.selectedYear}`], yData: [{ name: 'Count of Sector', type: 'line', data: [0] }] })
      return
    } else {
      const chartData = getArrangeMonthOrder(capacityFilters.selectedYear, capacityFilters.selectedMonth, chartResponse.data)
      const xData = chartData.map((obj) => `${obj.monthYear}`);
      const yData = chartData.flatMap((obj) => obj.categories)
        .reduce((acc, category) => {
          const { type, enodbID_count } = category;
          const existingCategory = acc.find((item) => item.name === type);

          if (existingCategory) {
            existingCategory.data.push(enodbID_count);
          } else {
            acc.push({ name: type, type: "bar", data: [enodbID_count] });
          }
          return acc;
        }, []);

      const sectorCounts = chartData.map((obj) => ({
        month: obj.month,
        secIDs: obj.secID_count,
      })).map((obj) => obj.secIDs);

      const formattedSectorCounts = {
        name: "Count of Sector",
        type: "line",
        data: sectorCounts,
      };
      yData.push(formattedSectorCounts);

      const chartObj = {
        xData: xData,
        yData: yData,
      };
      setCapacityChartData(chartObj);
    }
  }

  const onChangeCapacitySubFilters = async (isAdvanceEnable = false) => {
    let allFilters = {};
    let enod_arr = [];
    let sec_arr = []

    if (isAdvanceEnable) {
      enod_arr = selectedCapacityAdvanceData.map((item) => item.enodbid);
      sec_arr = selectedCapacityAdvanceData.flatMap((item) => item.sectors.map((sector) => sector.secID));
    }
    const filters = getCommonCapacityFilters(isAdvanceEnable, true)
    allFilters.enodbID = isAdvanceEnable && selectedCapacityAdvanceData.length ? enod_arr : capacityFilters.selectedTower ? [capacityFilters.selectedTower] : [];
    allFilters.secID = isAdvanceEnable && selectedCapacityAdvanceData.length ? sec_arr : capacityFilters.selectedSector ? [capacityFilters.selectedSector] : [];

    allFilters = { ...allFilters, ...filters };
    delete allFilters.action
    delete allFilters.flag
    const actionResponse = await getCapacitySubFilters(
      { ...allFilters, filterLvl: "sec_action" },
      false,
      null,
      null,
      null
    );

    const flagResponse = await getCapacitySubFilters(
      { ...allFilters, filterLvl: "sec_flag" },
      false,
      null,
      null,
      null
    );

    let allFlags = {};
    let allActions = {};

    actionResponse.data.length &&
      actionResponse.data.map((item) => {
        if (item._id !== "") {
          allActions[item._id] = {
            check: item.count ? true : false,
            editable: item.count ? false : true,
          };
        }
      });
    flagResponse.data.length &&
      flagResponse.data.map((item) => {
        if (item._id !== "") {
          allFlags[item._id] = {
            check: item.count ? true : false,
            editable: item.count ? false : true,
          };
        }
      });

    const selectedActions = Object.keys(allActions).filter(
      (key) => allActions[key].check === true
    );
    const selectedFlags = Object.keys(allFlags).filter(
      (key) => allFlags[key].check === true
    );

    setCapacityFilters((prev) => ({
      ...prev,
      selectedFlag: selectedFlags,
      selectedAction: selectedActions,
      selectedCapacitySubLvl: capacityFilters.selectedCapacitySubLvl || "flag",
    }));
    setCapacitySubFilters({
      ...capacitySubFilters,
      flags: allFlags,
      actions: allActions,
    });
  };

  const onClickCapacityEyeIcon = (isClicked = false) => {
    setEyeIcons({ ...eyeIcons, capacity: isClicked, supply: false, demand: false, marketShare: false, ibs: false });

    if (!isClicked) {
      setCapacityLegendData([])
    }
  }
  const onClickCapacityGeohash = async (params) => {
    if (!capacityTooltipData || capacityTooltipData?.geohash !== params.geohash) {
      let toolTipFilters = {}
      toolTipFilters = {
        geohash: params.geohash,
        enodbID: params.enodbID,
        secID: params.secID,
        operator: params.operator,
        month: params.month,
        countryCode: appliedFilters.countryCode,
        year: capacityFilters.selectedYear,
        flag: [params.flag] || [],
        action: [params.action] || []
      }

      try {
        const { data } = await getCapacityTooltipData({
          ...toolTipFilters
        });
        if (data) {
          const polygon = convertGeohashToPolygon(params.geohash);
          const selectedOp = operatorsPermittedCapacity.find(
            (x) => x.operator === params.operator);
          const tooltip = {
            ...params,
            polygon,
            header: {
              logo: selectedOp?.icon,
              name: selectedOp?.name,
              isAnonymized: selectedOp?.isAnonymized
            },
            body: [
              // {
              //   text: "Month",
              //   value: data.month || "No Data",
              //   txtBold: true
              // },
              // {
              //   text: "Year",
              //   value: data.year || "N/A",
              //   txtBold: true
              // },
              {
                text: "EnodeB ID",
                value: data.enodbID || "",
                txtBold: true
              },
              {
                text: "Sector ID",
                value: `${data.enodbID}_${data.secID}` || '',
                txtBold: true
              },
              {
                text: "Flag",
                value: data.flag || "",
                txtBold: true
              },
              {
                text: "Action",
                value: data.action || "",
                txtBold: true
              },
              {
                text: "Samples Count",
                value: data.n_samples || "",
                txtBold: true
              },
              {
                text: "RSRP (dBm)",
                value: data.median_rsrp || "",
                txtBold: true
              },
              {
                text: "RSRQ (dB)",
                value: data.median_rsrq || "",
                txtBold: true
              },
              {
                text: "SINR (dB)",
                value: data.median_sinr || "",
                txtBold: true
              },
              {
                text: "Traffic Volume (Kbytes)",
                value: data.trafficbytes ? parseFloat(data.trafficbytes).toFixed(6) : "",
                txtBold: true
              },
              {
                text: "DL Speed (Mbps)",
                value: data.downlink_avg ? parseFloat(data.downlink_avg).toFixed(6) : "",
                txtBold: true
              },
              {
                text: "UL Speed (Mbps)",
                value: data.uplink_avg ? parseFloat(data.uplink_avg).toFixed(6) : "",
                txtBold: true
              },

            ],
          };
          setCapacityTooltipData(tooltip);
        } else {
          toast.error(data, { theme: "colored" });
        }
      } catch (err) {
        toast.error("Error getting tooltip", { theme: "colored" });
      }
    }
  };

  const generateCapacityLegend = (isCapacityVisible) => {
    if (isCapacityVisible) {
      let capacityColorValuesArr = [];
      capacityLegend.forEach((item) => {
        item?.content.forEach((contItem) => {
          if (contItem.type === capacityFilters.selectedCapacitySubLvl) {
            contItem.values.forEach((obj) => {
              let colorObj = {};
              colorObj.text = obj?.text
              colorObj.markerType = obj?.colorType
              colorObj.markerColor = obj?.color
              capacityColorValuesArr.push(colorObj)
            })
            setCapacityLegendData([
              {
                title: contItem?.title,
                type: contItem?.type,
                values: capacityColorValuesArr
              },
            ]);
          }
        })
      })
    }
  };

  const getCommonCapacityFilters = (isAdvanceEnable = false, subFilterEnable = false, isFilterEnable = false, setCurrentBbox = false) => {
    let allFilters = {};
    const isAdminLvlEnable = appliedFilters.level && appliedFilters.level !== 'geoHashLevel' && appliedFilters.levelVal
    const isSubLvlEnable = capacityFilters.selectedCapacityLvl === 'enodbID' || capacityFilters.selectedCapacityLvl === 'secID'

    if (setCurrentBbox && mapRef?.current) {
      const ne = mapRef.current.getBounds().getNorthEast();
      const sw = mapRef.current.getBounds().getSouthWest();
      const BT = boxTolerance[mapRef.current.getZoom()] || 0;
      const neBound = [ne.lng() + BT, ne.lat() + BT]
      const swBound = [sw.lng() - BT, sw.lat() - BT]
      allFilters = {
        northEast: convertArrayToStringWithDelimeter(neBound, "_"),
        southWest: convertArrayToStringWithDelimeter(swBound, "_"),
      };
    } else {
      if (!isAdvanceEnable && !isAdminLvlEnable && !isSubLvlEnable && !isFilterEnable) {
        const { neBound, swBound } = getBbox;
        allFilters = {
          northEast: convertArrayToStringWithDelimeter(neBound, "_"),
          southWest: convertArrayToStringWithDelimeter(swBound, "_"),
        };
      }

      if (!isAdvanceEnable && appliedFilters.level === 'geoHashLevel' && geohashNE.length > 0 && geohashSW.length > 0) {
        allFilters = {
          northEast: convertArrayToStringWithDelimeter(geohashNE, "_"),
          southWest: convertArrayToStringWithDelimeter(geohashSW, "_"),
        };
      }
    }

    allFilters.level = isAdminLvlEnable ? appliedFilters.level : null;
    allFilters.levelVal = isAdminLvlEnable ? appliedFilters.levelVal : null;
    allFilters.operator = capacityFilters.selectedOprtr;
    allFilters.month = capacityFilters.selectedMonth;
    allFilters.countryCode = appliedFilters.countryCode || null
    allFilters.year = capacityFilters.selectedYear || null
    if (!isAdvanceEnable) {
      allFilters.flag = capacityFilters.selectedCapacitySubLvl === 'flag' && capacityFilters.selectedFlag ? capacityFilters.selectedFlag : []
      allFilters.action = capacityFilters.selectedCapacitySubLvl === 'action' && capacityFilters.selectedAction ? capacityFilters.selectedAction : []
    }
    if (!subFilterEnable) {
      allFilters.enodbID = capacityFilters.selectedTower || null
      allFilters.secID = capacityFilters.selectedSector || null
    }
    return allFilters
  }

  // check whether data can draw on map. Ex: eye icon ON and zoom lvl reached
  const canDrawDataOnMap = useMemo(() => {
    const { adminLevel, sites, marketShare, ...rest } = eyeIcons;
    const hasOpenEyeIcon = Object.values(rest).some((value) => value === true);
    return ((hasOpenEyeIcon && geohashZoom && mapZoomLevel >= geohashZoom) || // supply, demand, capacity, ibs
      (marketShare && msGeohashOnMap && geohashZoom && mapZoomLevel >= geohashZoom) ||
      ((edotcoSiteVisible || operatorSiteVisible) && sites && (mapZoomLevel >= siteZoom || mapZoomLevel >= clusterDefaultZoomLvl))); // sites
  }, [eyeIcons, mapZoomLevel, edotcoSiteVisible, operatorSiteVisible, geohashZoom, siteZoom]);

  return (
    <WebMapContext.Provider
      value={{
        onChangeFocusedAdminLvl,
        appliedFilters,
        onClickSupplyEyeIcon,
        onChangeSupplyType,
        onChangeSupplyBestOpToggle,
        onChangeCustomOpSupply,
        operatorDetails,
        defaultZoomLevels,
        supplyStrengthLevels,
        onClickDemandEyeIcon,
        onChangeDemandScore,
        onChangeDemandOperator,
        onChangeDemandNoDataToggle,
        onChangeDemandScoreRange,
        setSelectedEdotcoSiteOperator,
        selectedEdotcoSiteOperator,
        setSelectedProposedOperator,
        selectedProposedOperator,
        selectedOperatorSiteOperator,
        setSelectedOperatorSiteOperator,
        onClickSiteEyeIcon,
        onClickAdminLvlEyeIcon,
        generateSiteLegend,
        supplyFilters,
        demandFilters,
        setSupplyFilters,
        setMinMaxSupplyOp,
        minMaxSupplyOp,
        setDemandFilters,
        onChangeLevelFilterToggle,
        mapZoomLevel,
        supplyLvlFilters,
        affluenceLvlFilters,
        setAffluenceLvlFilters,
        mapTool,
        setMapTool,
        toolMarkers,
        setToolMarkers,
        onChangeSupplyConnectionType,
        onChangeSupplyDataType,
        resetSupplyToolTip,
        onChangeDemandConnectionType,
        onChangeDemandDataType,
        mapPlacement,
        setSelectedSiteRef,
        geohashZoom,
        setGeohashZoom,
        siteZoom,
        setSiteZoom,
        selectedPolygons,
        mapRef,
        onClickCapacityEyeIcon,
        setCapacityGeohashes,
        setCapacityTooltipData,
        eyeIcons,
        setCapacityChartData,
        setSelectedCapacityGeohashes,
        setCapacityFilters,
        capacityFilters,
        capacityFilterData,
        capacitySubFilters,
        setCapacitySubFilters,
        setCapacityFilterData,
        selectedCapacityAdvanceData,
        setSelectedCapacityAdvanceData,
        setCapacityAdvanceDisable,
        capacityAdvanceDisable,
        onChangeCapacitySubFilters,
        getCommonCapacityFilters,
        geohashNE,
        geohashSW,
        edotcoSiteAreaToggle,
        setEdotcoSiteAreaToggle,
        opSiteAreaToggle,
        setOpSiteAreaToggle,
        getPolygon,
        setSelectedPolygons,
        selectedSiteRefDropDown,
        setSelectedSiteRefDropDown,
        selectedSiteRef,
        getBbox,
        boundsChanged,
        edotcoSubFilters,
        setEdotcoSubFilters,
        proposedSiteVisible,
        setProposedSiteVisible,
        globalConfig,
        onClickMarketShareEyeIcon,
        msConfig,
        msConfigType,
        setMsConfig,
        onClickIbsEyeIcon,
        setMarketShareLegendData,
        setIBSLegendData,
        msTableColorConfig,
        storeLastDataLoadedBounds,
        setSearchAreaBtn,
        searchAreaBtn,
        setApisOngoing,
        selectedUploadedFile,
        setSelectedUploadedFile,
        triggerFetchUploadedFiles,
        setTriggerFetchUploadedFiles,
        isLoading,
        setIsLoading,
        ibsConfig,
        siteLegend,
        supplyLegend5G,
        supplyLegend4G,
        supplyLegend3G,
        demandLegend,
        capacityLegend,
        setMsGeohashOnMap,
        quadLabelsConfig,
        selectedCountryPremissions,
        bestOperatorsPermitted,
        downloadData,
        setDownloadData,
        downloadProperties,
        setDownloadProperties,
        enabledModules,
        setEnabledModules,
        filterOpPermissions,
        setPermittedOpsForDemand,
        setPermittedOpsForOpSite,
        setPermittedOpsForECSiteET,
        setPermittedOpsForECSitePT,
        operatorsPermittedCapacity,
        operatorsPermittedOpSite,
        operatorsPerfPermittedSupply,
        operatorsPermittedECSiteET,
        operatorsPermittedECSitePT,
        operatorsPermittedIBS,
        operatorsPermittedBestIBS,
        operatorsPermittedMS,
        operatorsPermittedTopMS
      }}
    >
      <GoogleMapView
        onMapIdle={onMapIdle}
        mapTypeId={mapTypeId}
        mapTool={mapTool}
        mapView={mapView}
        handleOnLoad={handleOnLoad}
        onChangeMapType={onChangeMapType}
        handleMapClick={handleMapClick}
        initSetting={initMapSettings}
        handleBoundsChange={handleBoundsChange}
      >
        {/* loading bar */}
        {isLoading ? <BackDrop /> : null}

        {/* components on the map */}
        <SidePanel setPosition={handleSearchedPosition} />
        <CurrentZoomLabel />
        <Legend
          legendData={[
            ...(((eyeIcons.demand || eyeIcons.supply) && geohashZoom && mapZoomLevel >= geohashZoom) && demandSupplyLegendData.length > 0 ? demandSupplyLegendData : []),
            ...((eyeIcons.capacity && geohashZoom && mapZoomLevel >= geohashZoom) && capacityLegendData.length > 0 ? capacityLegendData : []),
            ...edotcoSiteLegendData,
            ...operatorSiteLegendData,
            ...((eyeIcons.marketShare) && marketShareLegendData.length > 0 ? marketShareLegendData : []),
            ...((eyeIcons.ibs && geohashZoom && mapZoomLevel >= geohashZoom) && ibsLegendData.length > 0 ? ibsLegendData : []),
          ]}
          showLegend={
            (eyeIcons.supply && geohashZoom && mapZoomLevel >= geohashZoom) && demandSupplyLegendData.length > 0 ||
            (eyeIcons.demand && geohashZoom && mapZoomLevel >= geohashZoom) && demandSupplyLegendData.length > 0 ||
            (eyeIcons.capacity && geohashZoom && mapZoomLevel >= geohashZoom) && capacityLegendData.length > 0 ||
            edotcoSiteVisible ||
            operatorSiteVisible ||
            (eyeIcons.marketShare && marketShareLegendData.length > 0) ||
            (eyeIcons.ibs && geohashZoom && mapZoomLevel >= geohashZoom) && ibsLegendData.length > 0
          }
          LegendMinimized={(val) => setIsLegendMinimized(val)}
        />
        <MeasureToolTotalStats tool={mapTool} markers={toolMarkers} />
        {mapPlacement === 'right' &&
          <WebMapNoData apiCount={apisOngoing} canShow={canDrawDataOnMap} eyeIcons={eyeIcons} optrSiteVisible={operatorSiteVisible} ecoSiteVisible={edotcoSiteVisible} ibsVisible={ibsVisible} />}
        <WebMapTopNotifications showAreaMsg={searchAreaBtn} handleShow={setSearchAreaBtn} canShow={canDrawDataOnMap} optrSiteVisible={operatorSiteVisible} ecoSiteVisible={edotcoSiteVisible} msGeohashVisible={msGeohashOnMap} ibsVisible={ibsVisible} />

        <CapacityChart
          iconData={{
            capacity: eyeIcons.capacity && mapZoomLevel >= geohashZoom && capacityLegendData.length > 0,
            edotco: edotcoSiteVisible,
            operator: operatorSiteVisible
          }}
          capacityChartConfig={capacityChartConfig}
          chartData={capacityChartData}
          showChart={eyeIcons.capacity && capacityChartData && Object.keys(capacityChartData).length !== 0 ? true : false}
          mapPlacement={mapPlacement}
          mapView={mapView}
          isLegendMinimized={isLegendMinimized}
        />

        {/* Map sections & Side panel filtered data drawn on map */}
        <GeoAreaSection
          eyeIcons={eyeIcons}
          focusedPolygon={focusedPolygon}
        />
        {/* draw area geohash */}
        {eyeIcons.adminLevel && <SearchOnMapSection geohashOnMap={areaGeohashPolygon} />}
        {/* draw locator */}
        <SearchOnMapSection
          markerOnMap={markerOnMap}
          geohashOnMap={geohashOnMap}
        />
        <SupplyGeohashSection
          mapTool={mapTool}
          searchAreaBtn={searchAreaBtn}
          setSearchAreaBtn={setSearchAreaBtn}
          setTooltipData={setTooltipData}
          tooltipData={tooltipData}
          setApisOngoing={setApisOngoing}
          saveLastBound={storeLastDataLoadedBounds}
          permittedOperators={operatorsPerfPermittedSupply}
          bestOperatorsPermitted={bestOperatorsPermitted}
        />
        <DemandGeohashSection
          mapTool={mapTool}
          preferedGeohashZoom={preferedGeohashZoom}
          searchAreaBtn={searchAreaBtn}
          setSearchAreaBtn={setSearchAreaBtn}
          setTooltipData={setTooltipData}
          tooltipData={tooltipData}
          setApisOngoing={setApisOngoing}
          saveLastBound={storeLastDataLoadedBounds}
          operatorsPermittedDemand={operatorsPermittedDemand}
        />
        <SupplyDemandTooltipSection
          eyeIcons={eyeIcons}
          tooltipData={tooltipData}
          setTooltipData={setTooltipData}
          geohashZoom={geohashZoom}
        />
        <EdotcoSiteTooltipSection
          eyeIcons={eyeIcons}
          edotcoSiteTooltipData={edotcoSiteTooltipData}
          selectedEdotcoSiteOperator={selectedEdotcoSiteOperator}
          setEdotcoSiteTooltipData={setEdotcoSiteTooltipData}
          operatorsPermittedECSiteET={operatorsPermittedECSiteET}
          operatorsPermittedECSitePT={operatorsPermittedECSitePT}
        />
        <OprtSiteTooltipSection
          operatorSiteTooltipData={operatorSiteTooltipData}
          eyeIcons={eyeIcons}
          setOperatorSiteTooltipData={setOperatorSiteTooltipData}
          operatorsPermittedOpSite={operatorsPermittedOpSite}
        />
        <EdotcoSitesSection
          selectedSiteRef={selectedSiteRef}
          mapTool={mapTool}
          edotcoSubFilters={edotcoSubFilters}
          edotcoSiteVisible={edotcoSiteVisible}
          searchAreaBtn={searchAreaBtn}
          setSearchAreaBtn={setSearchAreaBtn}
          setApisOngoing={setApisOngoing}
          tooltipData={edotcoSiteTooltipData}
          setEdotcoSiteTooltipData={setEdotcoSiteTooltipData}
          saveLastBound={storeLastDataLoadedBounds}
          operatorsPermittedECSiteET={operatorsPermittedECSiteET}
        />

        <UploadedFilesSection />

        <OperatorSitesSection
          mapTool={mapTool}
          operatorSiteVisible={operatorSiteVisible}
          searchAreaBtn={searchAreaBtn}
          setSearchAreaBtn={setSearchAreaBtn}
          setApisOngoing={setApisOngoing}
          setOperatorSiteTooltipData={setOperatorSiteTooltipData}
          saveLastBound={storeLastDataLoadedBounds}
          operatorsPermittedOpSite={operatorsPermittedOpSite}
        />
        <ProposedSitesSection
          mapTool={mapTool}
          proposedSiteVisible={proposedSiteVisible}
          edotcoSiteVisible={edotcoSiteVisible}
          edotcoSubFilters={edotcoSubFilters}
          searchAreaBtn={searchAreaBtn}
          setSearchAreaBtn={setSearchAreaBtn}
          tooltipData={edotcoSiteTooltipData}
          setEdotcoSiteTooltipData={setEdotcoSiteTooltipData}
          setApisOngoing={setApisOngoing}
          saveLastBound={storeLastDataLoadedBounds}
          operatorsPermittedECSitePT={operatorsPermittedECSitePT}
        />
        <CapacityGeohashSection
          eyeIcons={eyeIcons}
          mapTool={mapTool}
          capacityGeohashes={capacityGeohashes}
          selectedCapacityGeohashes={selectedCapacityGeohashes}
          onClickCapacityGeohash={onClickCapacityGeohash}
          geohashZoom={geohashZoom}
          filterType={capacityFilters.selectedCapacitySubLvl}
          dataColors={capacityLegendData}
        />
        <CapacityTooltipSection
          eyeIcons={eyeIcons}
          tooltipData={capacityTooltipData}
          setTooltipData={setCapacityTooltipData}
          geohashZoom={geohashZoom}
        />
        <MapToolsSection
          mapTool={mapTool}
          toolMarkers={toolMarkers}
        />
      </GoogleMapView>
    </WebMapContext.Provider>
  );
}

export default memo(WebMapGoogleMap);
